import { useCallback, useEffect, useState } from "react";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import Button from "../../common/elements/Button";
import ConfirmDialog from "./ConfirmDialog";
import { serverErrMsg } from "../utils/validators";

const toDatetimeLocal = (dt) => {
    if (!dt) return '';
    try { return new Date(dt).toISOString().slice(0, 16); } catch { return ''; }
};

const fromDatetimeLocal = (s) => s ? new Date(s).toISOString() : null;

const fmtDate = (dt) => dt ? new Date(dt).toLocaleString() : '—';

const Field = ({ label, help, children }) => (
    <div className="field">
        <label className="label is-small">{label}</label>
        <div className="control">{children}</div>
        {help && <p className="help has-text-grey">{help}</p>}
    </div>
);

const AnalysisTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [detailsState, setDetailsState] = useState(buildLoadingState(null));
    const [isSaving, setIsSaving] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [isTogglingRaw, setIsTogglingRaw] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [form, setForm] = useState({ startDate: '', endDate: '' });
    const [prodDates, setProdDates] = useState({ firstActivated: null, lastPaused: null, useRawDecisions: false });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const fetchDetails = useCallback(() => {
        setDetailsState(buildLoadingState(null));
        execute('GET', thor_api_urls.experiment(experimentKey))
            .then(data => {
                const detail = data?.experiment ?? data;
                setDetailsState(buildDataState(detail));
                const analysis = detail?.analysis ?? detail?.experimentAnalysis ?? {};
                setForm({
                    startDate: toDatetimeLocal(analysis.startDate ?? detail?.analysisStartDate),
                    endDate:   toDatetimeLocal(analysis.endDate   ?? detail?.analysisEndDate),
                });
                setProdDates({
                    firstActivated:  detail?.prodEarliest ?? null,
                    lastPaused:      detail?.prodLatest   ?? null,
                    useRawDecisions: Boolean(detail?.useRawDecisions),
                });
            })
            .catch(err => {
                console.error("Failed to fetch analysis config:", err);
                setDetailsState(buildErrorState(err, null));
            });
    }, [execute, experimentKey]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const handleSave = () => {
        setIsSaving(true);
        execute('POST', thor_api_urls.experimentAnalysis(experimentKey), {
            startDate: fromDatetimeLocal(form.startDate),
            endDate:   fromDatetimeLocal(form.endDate),
        })
            .then(() => { addToast('Analysis dates saved.', 'success'); fetchDetails(); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to save analysis dates.'), 'danger'))
            .finally(() => setIsSaving(false));
    };

    const handleToggleRaw = (newVal) => {
        if (!editMode) return;
        setIsTogglingRaw(true);
        execute('POST', thor_api_urls.rawDecisionsFlag(experimentKey, newVal))
            .then(() => {
                setProdDates(p => ({ ...p, useRawDecisions: newVal }));
                addToast(`Use Raw Decisions ${newVal ? 'enabled' : 'disabled'}.`, 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to update raw decisions flag.'), 'danger'))
            .finally(() => setIsTogglingRaw(false));
    };

    const handleReset = () => {
        setIsResetting(true);
        execute('POST', thor_api_urls.resetExperiment(experimentKey))
            .then(() => { addToast('Experiment reset.', 'success'); fetchDetails(); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to reset experiment.'), 'danger'))
            .finally(() => setIsResetting(false));
    };

    const isInvalid = form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate);

    return (
        <StateHandler showLoader={detailsState.loading} showError={detailsState.error}
                      loadingScreen={<SmallLoadingSpinner />}>
            <div className="level mb-4">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">Analysis</p>
                </div>
                {editMode && (
                    <div className="level-right">
                        <Button className="is-link is-small" onClick={handleSave} disabled={isSaving || !!isInvalid}>
                            {isSaving ? 'Saving…' : 'Save Analysis'}
                        </Button>
                    </div>
                )}
            </div>

            <div className="notification is-info is-light mb-4">
                <p><strong>Analysis Configuration</strong> — this tab configures the Heimdall analysis pipeline (date window and settings).</p>
                <p className="mt-1">To view metric results, open <a href={`https://heimdall.internal/experiments/${experimentKey}`} target="_blank" rel="noopener noreferrer">Heimdall ↗</a>.</p>
            </div>

            {/* ── Important Production Dates ── */}
            <p className="has-text-weight-semibold is-size-7 has-text-grey mb-3"
               style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Important Production Dates
            </p>

            <div className="columns is-multiline mb-5">
                <div className="column is-half">
                    <Field label={<>Use Raw Bucketing Decisions<HelpTooltip text={HELP.analysis.useRawDecisions} /></>}>
                        {!editMode ? (
                            <p className="is-size-7 has-text-weight-semibold" style={{minHeight: '2em', paddingTop: '0.3em'}}>
                                {prodDates.useRawDecisions === true ? 'Yes' : prodDates.useRawDecisions === false ? 'No' : <span className="has-text-grey">—</span>}
                            </p>
                        ) : (
                            <div className="buttons has-addons">
                                <button type="button"
                                    className={`button is-small ${prodDates.useRawDecisions === true ? 'is-success is-selected' : ''}`}
                                    disabled={isTogglingRaw}
                                    onClick={() => handleToggleRaw(true)}>Yes</button>
                                <button type="button"
                                    className={`button is-small ${prodDates.useRawDecisions === false ? 'is-grey-light is-selected' : 'is-light has-text-grey'}`}
                                    disabled={isTogglingRaw}
                                    onClick={() => handleToggleRaw(false)}>No</button>
                            </div>
                        )}
                    </Field>
                </div>
                <div className="column is-half" />
                <div className="column is-half">
                    <Field label={<>First Activated<HelpTooltip text={HELP.analysis.firstActivated} /></>}>
                        <input className="input is-small" type="text" disabled
                               value={fmtDate(prodDates.firstActivated)} />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label={<>Last Paused<HelpTooltip text={HELP.analysis.lastPaused} /></>}>
                        <input className="input is-small" type="text" disabled
                               value={fmtDate(prodDates.lastPaused)} />
                    </Field>
                </div>
            </div>

            {/* ── Analysis Period ── */}
            <p className="has-text-weight-semibold is-size-7 has-text-grey mb-3"
               style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Analysis Period
            </p>

            <p className="has-text-grey is-size-7 mb-3">
                {HELP.analysis.general}
            </p>

            <div className="columns">
                <div className="column is-half">
                    <Field label={<>Start Date<HelpTooltip text={HELP.analysis.startDate} /></>}>
                        <input className="input is-small" type="datetime-local"
                               value={form.startDate} disabled={!editMode}
                               onChange={e => set('startDate', e.target.value)} />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label={<>End Date<HelpTooltip text={HELP.analysis.endDate} /></>}>
                        <input className="input is-small" type="datetime-local"
                               value={form.endDate} disabled={!editMode}
                               onChange={e => set('endDate', e.target.value)} />
                    </Field>
                </div>
            </div>

            {isInvalid && (
                <div className="notification is-warning is-light mt-2">
                    End date must be after start date.
                </div>
            )}

            {/* ── Reset Experiment ── */}
            <div style={{ marginTop: '2rem', borderTop: '1px solid #dbdbdb', paddingTop: '1.25rem' }}>
                <Button className="is-danger is-light is-small" disabled={!editMode || isResetting}
                        title={!editMode ? 'Enable Edit mode to reset' : 'Starts the experiment from scratch — clears Heimdall results and re-buckets all users'}
                        onClick={() => setConfirmReset(true)}>
                    {isResetting ? 'Resetting…' : 'Reset Experiment'}
                </Button>
                <p className="help has-text-grey mt-1">
                    Starts the experiment from scratch: clears analysis dates, clears past Heimdall results, and re-buckets all users. Irreversible.
                </p>
            </div>

            <ConfirmDialog
                isActive={confirmReset}
                title="Reset Experiment?"
                message={
                    <div>
                        <p className="mb-2">This will start the experiment from scratch:</p>
                        <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
                            <li>Clear analysis dates</li>
                            <li>Clear all past Heimdall results</li>
                            <li>Re-bucket all users (they will be assigned differently going forward)</li>
                        </ul>
                        <p className="has-text-danger has-text-weight-semibold">All results and metrics data prior to the reset will not be shown in Heimdall and will be lost. This action is irreversible.</p>
                        <p className="mt-2 has-text-grey is-size-7">Make sure the experiment is paused in production before resetting.</p>
                    </div>
                }
                confirmLabel="Reset"
                confirmClass="is-danger"
                onConfirm={() => { setConfirmReset(false); handleReset(); }}
                onCancel={() => setConfirmReset(false)}
            />
        </StateHandler>
    );
};

export default AnalysisTab;
